'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import { PayrollCutoff } from './technicianTypes';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Props {
  open: boolean;
  onClose: () => void;
  cutoff: PayrollCutoff;
  completedJobs: number;
  technicianId: string | null;
  onSubmitted?: () => void;
}

interface FormData {
  amount: number;
  reason: string;
}

export default function CashAdvanceModal({ open, onClose, cutoff, completedJobs, technicianId, onSubmitted }: Props) {
  const [loading, setLoading] = useState(false);
  const supabase = createClient();
  const grossPay = cutoff.daysWorked * cutoff.dailyRate + cutoff.totalHoursOT * cutoff.hourlyOTRate + cutoff.jobIncentives;
  const maxAdvance = Math.max(0, grossPay - cutoff.approvedCashAdvance);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  async function onSubmit(data: FormData) {
    if (Number(data.amount) > maxAdvance) {
      toast.error(`Request amount exceeds your accumulated earnings (₱${maxAdvance.toLocaleString()})`);
      return;
    }
    if (!technicianId) {
      toast.error('Technician profile not found');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('cash_advance_requests').insert({
        technician_id: technicianId,
        amount: Number(data.amount),
        cutoff_period: cutoff.period,
        cutoff_month: cutoff.month,
        cutoff_year: cutoff.year,
        status: 'pending',
        notes: data.reason,
      });

      if (error) {
        toast.error('Failed to submit request: ' + error.message);
        return;
      }
      toast.success(`Cash advance request of ₱${Number(data.amount).toLocaleString()} submitted to admin for approval`);
      reset();
      onClose();
      onSubmitted?.();
    } catch (err: any) {
      toast.error(err.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Request Cash Advance" size="sm">
      <div className="mb-4 bg-secondary rounded-xl p-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Cutoff Period</p>
            <p className="font-700 text-foreground">{cutoff.month} {cutoff.period}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Jobs Completed</p>
            <p className="font-700 text-foreground tabular-nums">{completedJobs}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Accumulated Gross</p>
            <p className="font-700 text-primary tabular-nums">₱{grossPay.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Max Requestable</p>
            <p className="font-700 text-success tabular-nums">₱{maxAdvance.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {maxAdvance <= 0 && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          <span>No accumulated earnings available for advance request in this cutoff period.</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs font-600 text-foreground mb-1">
            Requested Amount (₱) <span className="text-danger">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1">Maximum: ₱{maxAdvance.toLocaleString()}</p>
          <input
            type="number"
            {...register('amount', {
              required: 'Amount is required',
              min: { value: 1, message: 'Must be at least ₱1' },
              max: { value: maxAdvance, message: `Cannot exceed ₱${maxAdvance.toLocaleString()}` },
            })}
            placeholder="Enter amount"
            disabled={maxAdvance <= 0}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          {errors.amount && <p className="mt-1 text-xs text-danger">{errors.amount.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            {...register('reason', { required: 'Please provide a reason for the advance' })}
            rows={3}
            placeholder="Briefly explain why you need this advance..."
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          {errors.reason && <p className="mt-1 text-xs text-danger">{errors.reason.message}</p>}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-600 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || maxAdvance <= 0}
            className="flex-1 py-2.5 text-sm font-600 btn-primary rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" />Submitting...</> : 'Submit Request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}